import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import ActivityCard from "./ActivityCard";
import MatterWorkItemsCard from "./MatterWorkItemsCard";
import { messageRow, reviewRow, taskRow, vaRow } from "./activityRows";
import AssignTaskModal from "../Inbox/AssignTaskModal";
import VaSubmissionEvidenceModal from "../../pages/Home/VeteransAffairs/VaSubmissionEvidenceModal";
import {
  MATTER_ACTIVITY_SECTIONS,
  fetchMatterActivityList,
  setMatterActivityFilters,
  setMatterActivityMatter,
} from "../../slice/MatterActivitySlice";
import { appendAIPaneTo } from "../../utils/aiPaneUrl";
import { fetchVaUploadEvidence } from "../../service/home";

const readEvidenceError = (error) =>
  error?.response?.data?.detail ||
  error?.response?.data?.error ||
  error?.message ||
  "Unable to load VA submission evidence.";

/**
 * The four things anyone actually chases on a case, on the matter home
 * page: what went to the VA, what has been delegated, what small jobs are
 * outstanding, and what is with a colleague for review (Matthew,
 * 2026-07-30).
 *
 * Laid out two-up rather than as four full-width tables (Matthew,
 * 2026-07-30). The tables were the problem: seven columns of one-line
 * cells is a spreadsheet, and four of them stacked meant scrolling past
 * three lists to reach the fourth. Two columns of compact cards halves the
 * page height and lets each row put its details on their own lines. See
 * `ActivityCard` for the row shape and `activityRows.js` for what each
 * column became.
 *
 * The data underneath is unchanged: the same four Home endpoints called
 * with a `matter_id`, which swaps the question from "what is mine" to
 * "what is on this case, whoever's desk it is on".
 */

const Grid = styled.div`
  display: grid;
  /* minmax(0, 1fr) rather than 1fr: a grid track sized to content lets a
     long filename widen the column and break the two-up layout. */
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  /* Cards size to their own content instead of stretching to match the
     tallest in the row, which on a quiet case is mostly empty space. */
  align-items: start;
  margin-top: 18px;

  /* Two-up needs the width to be worth having. Below this the pair would
     be narrower than a single card wants to be, so they stack. */
  @media (max-width: 1200px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const CARDS = [
  {
    section: "va",
    title: "Submitted to VA",
    adapt: vaRow,
    emptyMessage: "Nothing submitted to the VA yet",
    sortable: true,
  },
  {
    section: "tasks",
    title: "Tasks",
    adapt: taskRow,
    emptyMessage: "No tasks on this matter",
  },
  {
    section: "reviews",
    title: "Doc Reviews",
    adapt: reviewRow,
    emptyMessage: "No document reviews on this matter",
    sortable: true,
  },
  {
    section: "messages",
    title: "Messages",
    adapt: messageRow,
    emptyMessage: "No messages on this matter",
  },
];

// Row shapes differ by section (a VA row is flat, a review row can nest
// its document), so the document id is looked for in both places rather
// than each card needing its own click handler.
const documentIdOf = (record) =>
  record?.document_id ?? record?.document?.id ?? null;

const MatterActivity = ({ matterId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [createTaskOpen, setCreateTaskOpen] = useState(false);
  // One Door (Matthew, 8/31): once this matter's Work Items loop is
  // live in the new Salesforce, the Work Items card owns creation and
  // the legacy Create Task button retires — same rule as the inbox.
  const [workItemsLive, setWorkItemsLive] = useState(false);

  // A VA row is a filing record, not a document link (a "Submitted to
  // VA" card can list uploads with no document open-able the same way a
  // task's deliverable is), so clicking one opens the same evidence
  // ledger the dedicated VA E-Submission page shows rather than trying
  // to navigate to a document.
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [evidenceError, setEvidenceError] = useState("");
  const [evidenceData, setEvidenceData] = useState(null);

  const {
    lists,
    status,
    errors,
    filters,
    matterId: loadedMatterId,
  } = useSelector((state) => state.matterActivity);

  useEffect(() => {
    if (matterId) {
      dispatch(setMatterActivityMatter(matterId));
    }
  }, [dispatch, matterId]);

  // Nothing is fetched until the slice has been pointed at this matter,
  // so a request can never go out carrying the previous case's filters.
  const ready = Boolean(matterId) && loadedMatterId === matterId;

  useEffect(() => {
    if (!ready) {
      return;
    }
    MATTER_ACTIVITY_SECTIONS.forEach((section) => {
      dispatch(fetchMatterActivityList({ matterId, section }));
    });
  }, [dispatch, matterId, ready]);

  // Per section rather than shared, so paging Tasks cannot move the
  // Assignments card's page, and only the card whose filters changed
  // refetches.
  const patchFilters = useCallback(
    (section, changes) => {
      dispatch(setMatterActivityFilters({ section, filters: changes }));
      dispatch(fetchMatterActivityList({ matterId, section }));
    },
    [dispatch, matterId]
  );

  const openDocument = useCallback(
    (record, openMessages = false) => {
      const documentId = documentIdOf(record);
      if (documentId) {
        if (openMessages) {
          // Deliberately forces the Messages tab, so it does not go
          // through the preserve-current-tab path below.
          navigate(`/matter/${matterId}/D${documentId}?ai=messages`, {
            state: { autoOpenMessages: true, messageThreadId: record.id },
          });
        } else {
          // Carry the AI panel's `?ai=` state across, or opening a
          // document from the dashboard closes the panel.
          navigate(
            appendAIPaneTo(`/matter/${matterId}/D${documentId}`, location.search)
          );
        }
      } else if (openMessages && record?.id) {
        navigate(`/matter/${matterId}?ai=messages`, {
          state: { autoOpenMessages: true, messageThreadId: record.id },
        });
      }
    },
    [navigate, matterId, location.search]
  );

  // Scope "firm" matches the visibility the row was loaded under: the
  // card's list request omits `scope` and carries only `matter_id`, so the
  // backend already defaulted it to firm-wide within this matter
  // (`VA_MATTER_DEFAULT_SCOPE`, matching every other matter-scoped VA
  // view). Reusing "mine" here would 404 on a colleague's submission that
  // is plainly on screen.
  const openEvidence = useCallback(async (record) => {
    setEvidenceOpen(true);
    setEvidenceLoading(true);
    setEvidenceError("");
    setEvidenceData(null);
    try {
      const response = await fetchVaUploadEvidence(record.id, "firm");
      setEvidenceData(response.data);
    } catch (error) {
      setEvidenceError(readEvidenceError(error));
    } finally {
      setEvidenceLoading(false);
    }
  }, []);

  const closeEvidence = useCallback(() => {
    setEvidenceOpen(false);
  }, []);

  return (
    <>
    <Grid data-testid="matter-activity">
      {/* Renders only on migrated matters; reports availability so the
          Tasks card can retire its Create Task button (One Door). */}
      <MatterWorkItemsCard
        matterId={matterId}
        onAvailability={setWorkItemsLive}
      />
      {CARDS.map(({ section, title, adapt, emptyMessage, sortable }) => {
        const sectionFilters = filters[section];
        const loaded = status[section] === "succeeded";

        return (
          <ActivityCard
            key={section}
            title={title}
            rows={lists[section]?.results || []}
            adapt={adapt}
            // A card that failed to load must not sit there reading as an
            // empty case -- on a matter, "nothing here" is a finding.
            emptyMessage={
              errors[section]
                ? "Could not be loaded. Refresh to try again."
                : emptyMessage
            }
            isLoading={status[section] === "loading"}
            count={loaded ? lists[section]?.count ?? 0 : undefined}
            currentPage={sectionFilters?.page || 1}
            pageSize={sectionFilters?.page_size}
            onPageChange={(page) => patchFilters(section, { page })}
            onRowClick={(record) =>
              section === "va"
                ? openEvidence(record)
                : openDocument(record, section === "messages")
            }
            headerExtra={
              section === "tasks" && !workItemsLive ? (
                <button
                  type="button"
                  onClick={() => setCreateTaskOpen(true)}
                  style={{
                    marginLeft: "auto",
                    border: "1px solid #d1d5db",
                    background: "#fff",
                    borderRadius: 4,
                    padding: "2px 8px",
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  Create Task
                </button>
              ) : null
            }
            onSort={
              sortable
                ? () =>
                    patchFilters(section, {
                      sort_direction:
                        sectionFilters?.sort_direction === "asc" ? "desc" : "asc",
                      // Page 4 of the old order is rarely a page of the new.
                      page: 1,
                    })
                : undefined
            }
          />
        );
      })}
    </Grid>
    <AssignTaskModal
      open={createTaskOpen}
      matterId={matterId}
      onClose={() => setCreateTaskOpen(false)}
      onCreated={() =>
        dispatch(fetchMatterActivityList({ matterId, section: "tasks" }))
      }
    />
    <VaSubmissionEvidenceModal
      open={evidenceOpen}
      onClose={closeEvidence}
      loading={evidenceLoading}
      error={evidenceError}
      evidenceData={evidenceData}
    />
    </>
  );
};

MatterActivity.propTypes = {
  matterId: PropTypes.string,
};

export default MatterActivity;
